import { Checkbox, Tooltip } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import ReactHotkeys from 'react-hot-keys';
import styled, { css } from 'styled-components';

import { CrossSvg, MenuArrowDownSvg } from '../../../../assets/icons';
import EnterprisePlanIcon from '../../../../assets/icons/EnterprisePlanIcon';
import theme from '../../../../assets/theme';
import { LicenseCoarseLevels } from '../../../../hooks/useAccessBadge';
import { useOnOutsideClick } from '../../../../hooks/useOnOutsideClick';
import usePaywallPrompt from '../../../../hooks/usePaywallPrompt';
import { PromptDropdownOption } from '../../../../services/backendService/types';
import { IconWrapper } from '../../../_atoms/IconWrapper';

export const GeneratorDropdown = ({
  label,
  onSelect,
  options,
  value,
}: {
  label: string;
  value: string | undefined;
  options: PromptDropdownOption[];
  onSelect: (value: string) => void;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const { isReady, promptPaywall, paywallComponent, userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  const handleEscapePress = () => {
    setShowDropdown(false);
  };

  const handleSelect = (value: string) => {
    const option = options.find(o => o.value === value);
    if (
      !option?.isEnterprise ||
      (option?.isEnterprise &&
        isReady &&
        userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise)
    ) {
      // Go on with the normal flow
      setShowDropdown(false);
      onSelect(value);
      return;
    }
    if (
      option?.isEnterprise &&
      isReady &&
      userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
    ) {
      promptPaywall({ label: 'Upgrade account' });
      return;
    }
  };

  const selectedOption = useMemo(() => {
    return options.find(o => o.value === value);
  }, [options, value]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnOutsideClick(wrapperRef, handleEscapePress);

  return (
    <Wrapper ref={wrapperRef}>
      {paywallComponent}
      {label && <Label>{label}</Label>}
      <FakeInput onClick={() => setShowDropdown(!showDropdown)}>
        <span style={{ flex: 1 }}>{selectedOption?.displayName}</span>
        <IconWrapper height="12px" width="12px">
          <MenuArrowDownSvg />
        </IconWrapper>
      </FakeInput>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: '100%',
                left: 0,
                background: 'white',
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                maxHeight: 250,
                overflowY: 'auto',
              }}
            >
              {options.map(option => (
                <OptionContainer
                  key={option.value}
                  $selected={option.value === value}
                  $isEnterprise={option.isEnterprise}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.displayName}
                  {option.isEnterprise && (
                    <Tooltip
                      title={
                        userAccessBadge?.license.coarseLevel ===
                        LicenseCoarseLevels.enterprise
                          ? "Available to you as you're on the school plan"
                          : 'Available on the school plan'
                      }
                    >
                      <IconWrapper
                        height="16px"
                        width="16px"
                        style={{
                          position: 'absolute',
                          paddingBottom: 2,
                          right: 10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color:
                            userAccessBadge?.license.coarseLevel ===
                            LicenseCoarseLevels.enterprise
                              ? theme.colors.black
                              : theme.colors.yellow,
                        }}
                      >
                        <EnterprisePlanIcon />
                      </IconWrapper>
                    </Tooltip>
                  )}
                </OptionContainer>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ReactHotkeys keyName="Escape" onKeyUp={handleEscapePress} />
    </Wrapper>
  );
};

export const GeneratorDropdownV2 = ({
  label,
  onSelect,
  options,
  value,
}: {
  label: string;
  value: string | undefined;
  options: PromptDropdownOption[];
  onSelect: (value: string) => void;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const { isReady, promptPaywall, paywallComponent, userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });

  const handleEscapePress = () => {
    setShowDropdown(false);
  };

  const handleSelect = (value: string) => {
    const option = options.find(o => o.value === value);
    if (
      !option?.isEnterprise ||
      (option?.isEnterprise &&
        isReady &&
        userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise)
    ) {
      // Go on with the normal flow
      setShowDropdown(false);
      onSelect(value);
      return;
    }

    if (
      option?.isEnterprise &&
      isReady &&
      userAccessBadge?.license.coarseLevel !== LicenseCoarseLevels.enterprise
    ) {
      promptPaywall({
        label: 'Generator Dropdown',
        description: `${selectedOption?.displayName}`,
      });
      return;
    }
  };

  const selectedOption = useMemo(() => {
    return options.find(o => o.value === value);
  }, [options, value]);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useOnOutsideClick(wrapperRef, handleEscapePress);

  return (
    <Wrapper ref={wrapperRef}>
      {paywallComponent}
      {label && <LabelV2>{label}</LabelV2>}
      <FakeInputV2 onClick={() => setShowDropdown(!showDropdown)}>
        <span style={{ flex: 1 }}>{selectedOption?.displayName}</span>
        <IconWrapper height="12px" width="12px">
          <MenuArrowDownSvg />
        </IconWrapper>
      </FakeInputV2>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: '100%',
                left: 0,
                background: 'white',
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                maxHeight: 250,
                overflowY: 'auto',
              }}
            >
              {options.map(option => (
                <OptionContainer
                  key={option.value}
                  $selected={option.value === value}
                  $isEnterprise={option.isEnterprise}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.displayName}
                  {option.isEnterprise && (
                    <Tooltip
                      title={
                        userAccessBadge?.license.coarseLevel ===
                        LicenseCoarseLevels.enterprise
                          ? "Available to you as you're on the school plan"
                          : 'Available on the school plan'
                      }
                    >
                      <IconWrapper
                        height="16px"
                        width="16px"
                        style={{
                          position: 'absolute',
                          paddingBottom: 2,
                          right: 10,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color:
                            userAccessBadge?.license.coarseLevel ===
                            LicenseCoarseLevels.enterprise
                              ? theme.colors.black
                              : theme.colors.yellow,
                        }}
                      >
                        <EnterprisePlanIcon />
                      </IconWrapper>
                    </Tooltip>
                  )}
                </OptionContainer>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ReactHotkeys keyName="Escape" onKeyUp={handleEscapePress} />
    </Wrapper>
  );
};

const OptionContainer = styled.div<{
  $selected?: boolean;
  $isEnterprise?: boolean;
  $disabled?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  &:hover {
    background: ${theme.colors.greyLight};
  }
  user-select: none;

  ${({ $selected }) =>
    $selected &&
    css`
      background: #f6ece0;
      font-weight: bold;
    `}
  ${({ $isEnterprise }) =>
    $isEnterprise &&
    css`
      padding: 10px 35px 10px 15px;
    `}
  ${({ $disabled }) =>
    $disabled &&
    css`
      color: ${theme.colors.iconGrey};
      /* text-decoration: line-through; */
      cursor: not-allowed;
      &:hover {
        background: transparent;
      }
    `}
`;

const FakeInput = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  line-height: 22px;
  font-size: 16px;
  outline: none;
  border: 1px solid rgb(217, 217, 217);
  border-radius: 5px;
  padding: 10px 15px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  box-sizing: border-box;
  overflow: hidden;

  &:hover,
  &:focus {
    border: 1px solid ${theme.colors.green};
  }
`;

const FakeInputV2 = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  line-height: 22px;
  font-size: 16px;
  outline: none;
  border: 1px solid rgb(217, 217, 217);
  border-radius: 5px;
  padding: 10px 15px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  background: transparent;
  color: white;
  &:hover,
  &:focus {
    border: 1px solid white;
  }
`;

const Label = styled.div`
  color: ${theme.colors.black};
  font-family: 'Nunito';
  font-style: normal;
  font-weight: bold;
  font-size: 12px;
  line-height: 18px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
`;

const LabelV2 = styled.div`
  color: #fff;

  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Nunito;
  font-size: 18px;
  font-style: normal;
  font-weight: 800;
  line-height: 140%;
`;

const Wrapper = styled.div<{ $fluid?: boolean }>`
  cursor: pointer;
  position: relative;
  min-width: 0;
  width: 100%;
`;

export const GeneratorMultiSelect = ({
  label,
  onSelect,
  options,
  maxSelections,
  variant = 'default',
  footerLeft,
}: {
  label: string;
  value: string | undefined;
  options: PromptDropdownOption[];
  onSelect: (value: string) => void;
  maxSelections?: number;
  variant?: 'default' | 'v2';
  footerLeft?: React.ReactNode;
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOptionValues, setSelectedOptionValues] = useState<string[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const atLimit = maxSelections != null && selectedOptionValues.length >= maxSelections;
  const hasSelections = selectedOptionValues.length > 0;

  const buildOutputString = useCallback(
    (selected: string[]) => {
      return selected
        .map(v => options.find(o => o.value === v)?.displayName ?? v)
        .join(', ');
    },
    [options],
  );

  const handleToggle = useCallback(
    (optionValue: string) => {
      const isSelected = selectedOptionValues.includes(optionValue);
      if (!isSelected && atLimit) return;
      const next = isSelected
        ? selectedOptionValues.filter(v => v !== optionValue)
        : [...selectedOptionValues, optionValue];
      setSelectedOptionValues(next);
      onSelect(buildOutputString(next));
    },
    [selectedOptionValues, onSelect, atLimit, buildOutputString],
  );

  const handleDeselectAll = useCallback(() => {
    setSelectedOptionValues([]);
    onSelect('');
  }, [onSelect]);

  const handleDone = useCallback(() => {
    setShowDropdown(false);
  }, []);

  const displayText = useMemo(() => {
    if (selectedOptionValues.length === 0) return 'Click to select...';
    return buildOutputString(selectedOptionValues);
  }, [selectedOptionValues, buildOutputString]);

  useOnOutsideClick(wrapperRef, () => setShowDropdown(false));

  return (
    <Wrapper ref={wrapperRef}>
      {label && (variant === 'v2' ? <LabelV2>{label}</LabelV2> : <Label>{label}</Label>)}
      <MultiSelectFakeInput onClick={() => setShowDropdown(!showDropdown)}>
        <MultiSelectText>{displayText}</MultiSelectText>
        <IconWrapper height="12px" width="12px" style={{ flexShrink: 0 }}>
          <MenuArrowDownSvg />
        </IconWrapper>
      </MultiSelectFakeInput>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: '100%',
                left: 0,
                background: 'white',
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 300,
              }}
            >
              <DropdownHeader>
                <SelectionCount>
                  {maxSelections != null
                    ? `${selectedOptionValues.length}/${maxSelections} selected`
                    : `${selectedOptionValues.length} selected`}
                </SelectionCount>
                <DeselectAllButton
                  type="button"
                  disabled={!hasSelections}
                  onClick={handleDeselectAll}
                >
                  Deselect all
                </DeselectAllButton>
              </DropdownHeader>
              <DropdownOptionsList>
                {options.map(option => {
                  const isChecked = selectedOptionValues.includes(option.value);
                  const isDisabled = !isChecked && atLimit;
                  return (
                    <OptionContainer
                      key={option.value}
                      $selected={isChecked}
                      onClick={() => handleToggle(option.value)}
                      style={
                        isDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined
                      }
                    >
                      <Checkbox
                        checked={isChecked}
                        disabled={isDisabled}
                        style={{ marginRight: 8, pointerEvents: 'none' }}
                      />
                      {option.displayName}
                    </OptionContainer>
                  );
                })}
              </DropdownOptionsList>
              <DropdownFooter>
                {footerLeft && <FooterLeftContent>{footerLeft}</FooterLeftContent>}
                <DoneButton type="button" onClick={handleDone}>
                  Done
                </DoneButton>
              </DropdownFooter>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ReactHotkeys keyName="Escape" onKeyUp={() => setShowDropdown(false)} />
    </Wrapper>
  );
};

export const GeneratorCombobox = ({
  label,
  onSelect,
  options,
  value,
  variant = 'default',
}: {
  label: string;
  value: string | undefined;
  options: PromptDropdownOption[];
  onSelect: (value: string) => void;
  variant?: 'default' | 'v2';
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? '');
  const [isTyping, setIsTyping] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const visibleOptions = useMemo(() => {
    if (!isTyping || !inputValue) return options;
    const lower = inputValue.toLowerCase();
    return options.filter(o => o.displayName.toLowerCase().includes(lower));
  }, [options, inputValue, isTyping]);

  const handleSelectOption = useCallback(
    (optionValue: string) => {
      const option = options.find(o => o.value === optionValue);
      setInputValue(option?.displayName ?? optionValue);
      setIsTyping(false);
      onSelect(optionValue);
      setShowDropdown(false);
    },
    [options, onSelect],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setInputValue(val);
      setIsTyping(true);
      onSelect(val);
      if (!showDropdown) setShowDropdown(true);
    },
    [onSelect, showDropdown],
  );

  const handleClear = useCallback(() => {
    setInputValue('');
    setIsTyping(false);
    onSelect('');
    setShowDropdown(false);
  }, [onSelect]);

  useOnOutsideClick(wrapperRef, () => {
    setShowDropdown(false);
    setIsTyping(false);
  });

  return (
    <Wrapper ref={wrapperRef}>
      {label && (variant === 'v2' ? <LabelV2>{label}</LabelV2> : <Label>{label}</Label>)}
      <ComboboxInputWrapper>
        <ComboboxInput
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          onClick={() => setShowDropdown(true)}
          placeholder="Type or select..."
          maxLength={500}
        />
        {inputValue && (
          <ClearButton
            onClick={e => {
              e.stopPropagation();
              handleClear();
            }}
          >
            <IconWrapper height="10px" width="10px">
              <CrossSvg />
            </IconWrapper>
          </ClearButton>
        )}
      </ComboboxInputWrapper>
      <div style={{ position: 'relative', width: '100%' }}>
        <AnimatePresence>
          {showDropdown && visibleOptions.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 2,
                width: '100%',
                left: 0,
                background: 'white',
                zIndex: 9999,
                boxShadow: theme.effects.defaultShadow,
                borderRadius: 5,
                maxHeight: 250,
                overflowY: 'auto',
              }}
            >
              {visibleOptions.map(option => (
                <OptionContainer
                  key={option.displayName}
                  $selected={option.value === value}
                  $disabled={option.disabled}
                  onClick={() => {
                    if (option.disabled) return;
                    handleSelectOption(option.value);
                  }}
                >
                  {option.displayName}
                </OptionContainer>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <ReactHotkeys keyName="Escape" onKeyUp={() => setShowDropdown(false)} />
    </Wrapper>
  );
};

const MultiSelectFakeInput = styled(FakeInput)`
  min-height: 44px;
  max-height: 44px;
  overflow: hidden;
  max-width: 100%;
  background: white;
  border: none;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};

  &:hover,
  &:focus {
    border: none;
    box-shadow: ${theme.effects.floatingCreatorElementShadowHovered};
  }
`;

const MultiSelectText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 15px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
`;

const SelectionCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.iconGrey};
`;

const DeselectAllButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.red};
  cursor: pointer;
  font-family: 'Nunito', sans-serif;
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:disabled {
    color: ${theme.colors.iconGrey};
    cursor: default;
  }
`;

const DropdownOptionsList = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const DropdownFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 12px;
  border-top: 1px solid #eee;
  flex-shrink: 0;
  background: ${theme.colors.paleBeige};
  border-radius: 0 0 5px 5px;
  gap: 8px;
`;

const FooterLeftContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const DoneButton = styled.button`
  background: ${theme.colors.orange};
  color: white;
  border: none;
  border-radius: 5px;
  padding: 6px 20px;
  font-size: 13px;
  font-weight: 700;
  font-family: 'Nunito', sans-serif;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 0.9;
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
  }
`;

const ComboboxInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
`;

const ComboboxInput = styled.input`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  line-height: 22px;
  font-size: 16px;
  outline: none;
  border: none;
  border-radius: 5px;
  padding: 10px 35px 10px 15px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  font-family: 'Nunito', sans-serif;
  /* Match the bold weight AntD Selects inherit from globals.css (p/span are
     bold): <input> isn't covered by that rule, so set it here to keep combobox
     values visually consistent with the other dropdown fields. */
  font-weight: bold;
  background: white;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};

  &:hover,
  &:focus {
    box-shadow: ${theme.effects.floatingCreatorElementShadowHovered};
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: ${theme.colors.greyLight};
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: ${theme.colors.grey};
  }
`;
